import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SecondFactor, SecondFactorMethod } from '@shared/api/contracts/security';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tabs,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@shared/ui';

export interface ReasonFactorValue {
  reason: string;
  lockOwner: boolean;
  secondFactor: SecondFactor;
}

interface ReasonFactorDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant?: 'default' | 'destructive' | 'secondary' | 'outline';
  reasonRequired?: boolean;
  showLockOwner?: boolean;
  lockOwnerLabel?: string;
  loading?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (value: ReasonFactorValue) => void;
}

const factorReady = (method: SecondFactorMethod, code: string) =>
  method === 'backup' ? code.trim().length >= 4 : code.trim().length >= 6;

export function ReasonFactorDialog({
  open,
  title,
  description,
  confirmLabel,
  confirmVariant = 'default',
  reasonRequired = true,
  showLockOwner = false,
  lockOwnerLabel,
  loading = false,
  onOpenChange,
  onConfirm,
}: ReasonFactorDialogProps) {
  const { t } = useTranslation('cabinet');
  const [method, setMethod] = useState<SecondFactorMethod>('totp');
  const [code, setCode] = useState('');
  const [reason, setReason] = useState('');
  const [lockOwner, setLockOwner] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMethod('totp');
    setCode('');
    setReason('');
    setLockOwner(false);
    setError(null);
  }, [open]);

  const submit = () => {
    const trimmedReason = reason.trim();
    if (reasonRequired && (trimmedReason.length < 3 || trimmedReason.length > 1000)) {
      setError(t('compliance.factor.reason'));
      return;
    }
    if (!factorReady(method, code)) {
      setError(t('security.factor.invalid'));
      return;
    }
    onConfirm({
      reason: trimmedReason,
      lockOwner: showLockOwner && lockOwner,
      secondFactor: { method, code: code.trim() },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {reasonRequired ? (
            <div className="space-y-2">
              <Label htmlFor="compliance-reason">{t('compliance.factor.reasonLabel')}</Label>
              <Textarea
                id="compliance-reason"
                value={reason}
                maxLength={1000}
                rows={4}
                onChange={(event) => setReason(event.target.value)}
              />
            </div>
          ) : null}
          {showLockOwner ? (
            <label className="flex items-start gap-3 text-sm text-foreground">
              <Checkbox
                checked={lockOwner}
                onCheckedChange={(checked) => setLockOwner(checked === true)}
                className="mt-0.5"
              />
              <span>{lockOwnerLabel}</span>
            </label>
          ) : null}
          <Tabs value={method} onValueChange={(value) => setMethod(value as SecondFactorMethod)}>
            <TabsList className="w-full">
              <TabsTrigger value="totp">{t('security.factor.totp')}</TabsTrigger>
              <TabsTrigger value="backup">{t('security.factor.backup')}</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="space-y-2">
            <Label htmlFor="compliance-factor-code">{t('compliance.factor.code')}</Label>
            <Input
              id="compliance-factor-code"
              value={code}
              autoComplete="one-time-code"
              inputMode={method === 'totp' ? 'numeric' : 'text'}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={loading} onClick={() => onOpenChange(false)}>
            {t('compliance.factor.cancel')}
          </Button>
          <Button type="button" variant={confirmVariant} loading={loading} onClick={submit}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
